'use client';

import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import { PeekIconStatic } from '@/components/icons/peek-icon-static';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth/auth-context';
import { PageBuilderProvider, PageBuilderForm } from '@/components/page-builder';
import { submitProduct } from '@/lib/services/productService';
import { inviteUser } from '@/lib/services/teamService';
import { Skeleton } from '@/components/ui/skeleton';
import { ThemeToggle } from '@/components/navigation/theme-toggle';
import { UserMenu } from '@/components/navigation/user-menu';
import type { PageBuilderFormData } from '@/lib/types/page-builder';
import { blocksToMedia } from '@/lib/page-builder/media-mapping';

// ============================================================================
// SUBMIT HANDLER
// ============================================================================

function useSubmitHandler() {
  const router = useRouter();

  const handleSubmit = useCallback(
    async (data: PageBuilderFormData) => {
      // Convert blocks to media array (new API format)
      const mediaItems = blocksToMedia(data.blocks);

      // Prepare submission data with new API format
      const submissionData = {
        slug: data.header.slug,
        name: data.header.name,
        tagline: data.header.tagline,
        description: data.header.description,
        websiteUrl: data.header.websiteUrl || '',
        logo: data.header.logo!,
        media: mediaItems,
        previewImages: data.metadata.previewImages?.map(img => img.imageKey) || [],
        highlights: [] as string[],
        categoryIds: data.metadata.categoryIds,
        statusTags: data.metadata.statusTags,
        launchDate: data.metadata.launchDate,
      };

      const result = await submitProduct(submissionData);

      if (result.success && result.data) {
        const productSlug = result.data.slug || result.data.id;

        // Send pending team invitations
        const pendingInvitations = data.metadata.pendingInvitations || [];
        if (pendingInvitations.length > 0) {
          const inviteResults = await Promise.allSettled(
            pendingInvitations.map((inv) =>
              inviteUser(productSlug, inv.user.id, inv.role)
            )
          );

          // Count successes/failures
          const succeeded = inviteResults.filter(
            (r) => r.status === 'fulfilled' && r.value.success
          ).length;
          const failed = pendingInvitations.length - succeeded;

          if (failed > 0 && succeeded > 0) {
            toast.warning(`Отправлено ${succeeded} из ${pendingInvitations.length} приглашений`);
          } else if (failed > 0 && succeeded === 0) {
            toast.error('Не удалось отправить приглашения');
          }
        }

        router.push(`/products/${productSlug}`);
      } else {
        throw new Error(result.error?.message || 'Failed to submit');
      }
    },
    [router]
  );

  return handleSubmit;
}

// ============================================================================
// PAGE COMPONENT
// ============================================================================

export default function SubmitPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const handleSubmit = useSubmitHandler();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login('/submit');
    }
  }, [isAuthenticated, isLoading, login]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Skeleton className="h-32 w-full rounded-xl mb-6" />
          <Skeleton className="h-48 w-full rounded-xl mb-6" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  // Redirect state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Перенаправляем на вход...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex h-14 items-center justify-between gap-4">
            {/* Left: Logo */}
            <Link
              href="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <PeekIconStatic className="h-8 w-8" />
              <div className="flex flex-col leading-none">
                <span className="text-base font-bold tracking-tight">
                  Peek Product
                </span>
                <span className="text-xs text-muted-foreground hidden sm:block">
                  Добавить продукт
                </span>
              </div>
            </Link>

            {/* Right: Theme + User */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Page Builder */}
      <PageBuilderProvider onSubmit={handleSubmit}>
        <PageBuilderForm />
      </PageBuilderProvider>
    </div>
  );
}
